/**
 * Scroll Theme Controller
 * Changes theme based on which themed section is currently in view
 */

import { setTheme } from './theme';

export const initScrollTheme = (options = {}) => {
  const defaults = {
    threshold: 0.2,
    rootMargin: '0px',
    sectionSelector: '[data-section-theme]',
    disableWhen: null,
  };

  const settings = { ...defaults, ...options };
  let observer;
  let sections = [];
  let isEnabled = true;
  let defaultTheme = document.body.getAttribute('data-theme') || 'white';
  let activeThemeSection = null;
  let currentTheme = defaultTheme;

  // Get all themed sections
  const findThemeSections = () => {
    sections = Array.from(document.querySelectorAll(settings.sectionSelector));
    return sections;
  };

  // Check if any themed section is in the viewport
  const checkIfAnySectionVisible = () => {
    // If we have an active section, check if it's still visible
    if (activeThemeSection) {
      const rect = activeThemeSection.getBoundingClientRect();
      if (rect.top >= window.innerHeight || rect.bottom <= 0) {
        // Section is no longer visible
        activeThemeSection = null;
        return false;
      }
      return true;
    }
    return false;
  };

  // Handle intersection
  const handleIntersect = (entries) => {
    if (!isEnabled) return;

    // Get all currently intersecting sections
    const intersectingSections = entries.filter(entry => entry.isIntersecting);
    
    if (intersectingSections.length > 0) {
      // Get the topmost visible section
      const section = intersectingSections[0].target;
      const theme = section.getAttribute('data-section-theme');
      
      if (theme) {
        activeThemeSection = section;
        currentTheme = theme;
        setTheme(theme);
      }
    } else {
      // Check if the previously active section is now out of view
      const exitingSection = entries.find(entry => 
        !entry.isIntersecting && entry.target === activeThemeSection
      );
      
      if (exitingSection) {
        // Our active section has left the viewport - reset to default theme
        activeThemeSection = null;
        currentTheme = defaultTheme;
        setTheme(defaultTheme);
      } else if (!checkIfAnySectionVisible()) {
        // No themed sections are visible - restore default
        currentTheme = defaultTheme;
        setTheme(defaultTheme);
      }
    }
  };

  // Setup observer
  const setupObserver = () => {
    if (observer) {
      sections.forEach(section => observer.unobserve(section));
    }
    
    observer = new IntersectionObserver(handleIntersect, {
      threshold: settings.threshold,
      rootMargin: settings.rootMargin
    });
    
    sections.forEach(section => {
      observer.observe(section);
    });
  };

  // Toggle enabled state
  const setEnabled = (enabled) => {
    isEnabled = enabled;
    
    // No longer reset theme when disabled - preserve current theme
  };

  // Initialize
  const init = () => {
    // Save default theme from body tag
    defaultTheme = document.body.getAttribute('data-theme') || 'white';
    currentTheme = defaultTheme;
    
    // Find sections and setup observer
    findThemeSections();
    setupObserver();
    
    // Set up disable condition if provided
    if (settings.disableWhen) {
      const checkDisableCondition = () => {
        setEnabled(!settings.disableWhen());
      };
      
      window.addEventListener('scroll', checkDisableCondition, { passive: true });
      window.addEventListener('resize', checkDisableCondition, { passive: true });
      checkDisableCondition();
    }
    
    // Watch for DOM changes
    const mutationObserver = new MutationObserver(() => {
      findThemeSections();
      setupObserver();
    });
    
    mutationObserver.observe(document.body, { 
      childList: true, 
      subtree: true 
    });
    
    // Add a scroll listener as a backup to ensure we check visibility
    window.addEventListener('scroll', () => {
      if (!checkIfAnySectionVisible() && activeThemeSection && isEnabled) {
        activeThemeSection = null;
        currentTheme = defaultTheme;
        setTheme(defaultTheme);
      }
    }, { passive: true });
  };

  // Start the system
  init();

  // Return public API
  return {
    enable: () => setEnabled(true),
    disable: () => setEnabled(false),
    isEnabled: () => isEnabled,
    refresh: () => {
      findThemeSections();
      setupObserver();
    },
    reset: () => {
      activeThemeSection = null;
      currentTheme = defaultTheme;
      setTheme(defaultTheme);
    },
    getCurrentTheme: () => currentTheme
  };
};